/**
 * @file ComplianceFrameworks.js
 * WHAT: CRUD management interface for compliance framework reference data (e.g., SOX, GDPR, HIPAA, PCI, ISO27001).
 * WHY: Compliance frameworks drive which applications require user access reviews -- each application is tagged with applicable frameworks, and campaigns are scoped accordingly to satisfy regulatory audit requirements.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSafeTimeout } from '../hooks';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Renders a table of compliance framework records with add/edit/delete modals, active/inactive status toggling, and refresh capability.
 * WHY: Admins need to maintain the list of compliance frameworks so that applications can be tagged with applicable regulations, enabling DAMA to scope attestation campaigns to the right compliance requirements.
 */
const ComplianceFrameworks = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentFramework, setCurrentFramework] = useState({
    framework_name: '',
    framework_description: '',
    is_active: 'Y'
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    fetchFrameworks();
  }, []);

  /* WHAT: Fetches all compliance frameworks from the backend, sorted by ID.
     WHY: Populates the main table; supports both initial load and manual refresh with distinct UX feedback. */
  const fetchFrameworks = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('config/compliance-frameworks/all/'));
      if (response.data.success) {
        // Sort frameworks by framework_id (numeric sort)
        const sortedFrameworks = response.data.frameworks.sort((a, b) => {
          return a.framework_id - b.framework_id;
        });
        setFrameworks(sortedFrameworks);
        if (isRefresh) {
          setSuccessMessage('Data refreshed successfully');
          setSafeTimeout(() => setSuccessMessage(''), 3000);
        }
      }
    } catch (error) {
      console.error('Error fetching frameworks:', error);
      setErrorMessage('Failed to load compliance frameworks');
      setSafeTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* WHAT: Triggers a data refresh with visual feedback (spinner on the Refresh button).
     WHY: Lets admins pull the latest data without a full page reload. */
  const handleRefresh = () => {
    fetchFrameworks(true);
  };

  /* WHAT: Opens the add-framework modal with a blank form.
     WHY: Resets form state to defaults so previous edit values don't leak into a new creation. */
  const handleAdd = () => {
    setIsEdit(false);
    setCurrentFramework({
      framework_name: '',
      framework_description: '',
      is_active: 'Y'
    });
    setErrors({});
    setShowModal(true);
  };

  /* WHAT: Opens the edit modal pre-populated with the selected framework's data.
     WHY: Converts backend boolean is_active to 'Y'/'N' for the select dropdown form control. */
  const handleEdit = (framework) => {
    setIsEdit(true);
    // Convert boolean to 'Y'/'N' for the form
    setCurrentFramework({
      ...framework,
      is_active: framework.is_active ? 'Y' : 'N'
    });
    setErrors({});
    setShowModal(true);
  };

  /* WHAT: Opens the delete confirmation modal for the specified framework.
     WHY: Deletion is destructive and irreversible, so a confirmation step is required before proceeding. */
  const handleDelete = (frameworkName) => {
    setDeleteTarget(frameworkName);
    setShowDeleteModal(true);
  };

  /* WHAT: Sends the DELETE request to remove the framework and handles success/error feedback.
     WHY: The backend may reject deletion if the framework is in use by applications; errors are shown in a modal. */
  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await axios.delete(API_CONFIG.getDatabasesEndpoint(`config/compliance-frameworks/${deleteTarget}/delete/`));
      if (response.data.success) {
        setSuccessMessage('Compliance framework deleted successfully');
        setSafeTimeout(() => setSuccessMessage(''), 3000);
        fetchFrameworks();
        setShowDeleteModal(false);
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error('Error deleting framework:', error);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      // Show error in modal
      setErrorModalMessage(error.response?.data?.error || 'Failed to delete compliance framework');
      setShowErrorModal(true);
    } finally {
      setDeleting(false);
    }
  };

  /* WHAT: Validates the framework form -- name is required and must not contain spaces.
     WHY: Framework names are used as unique identifiers in URLs and database keys, so spaces are disallowed. */
  const validateForm = () => {
    const newErrors = {};

    if (!currentFramework.framework_name.trim()) {
      newErrors.framework_name = 'Framework Name is required';
    }

    if (currentFramework.framework_name.trim() && /\s/.test(currentFramework.framework_name)) {
      newErrors.framework_name = 'Framework Name cannot contain spaces';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* WHAT: Submits the framework form as either a create (POST) or update (PUT) based on isEdit mode.
     WHY: Converts 'Y'/'N' back to boolean for the backend and refreshes the table on success. */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Convert 'Y'/'N' to boolean for backend
      const dataToSend = {
        ...currentFramework,
        is_active: currentFramework.is_active === 'Y'
      };

      console.log('Submitting framework data:', dataToSend);
      console.log('Is edit mode:', isEdit);

      let response;
      if (isEdit) {
        const url = API_CONFIG.getDatabasesEndpoint(`config/compliance-frameworks/${currentFramework.framework_name}/update/`);
        console.log('PUT URL:', url);
        response = await axios.put(url, {
          ...dataToSend,
          modified_by: 'ADMIN'
        });
      } else {
        response = await axios.post(
          API_CONFIG.getDatabasesEndpoint('config/compliance-frameworks/create/'),
          {
            ...dataToSend,
            created_by: 'ADMIN'
          }
        );
      }

      console.log('Response:', response.data);

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setSafeTimeout(() => setSuccessMessage(''), 3000);
        setShowModal(false);
        fetchFrameworks();
      }
    } catch (error) {
      console.error('Error saving framework:', error);
      console.error('Error response:', error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to save compliance framework');
      setSafeTimeout(() => setErrorMessage(''), 5000);
    }
  };

  /* WHAT: Updates form state on input change and clears field-level validation errors.
     WHY: Immediate error clearing gives the user responsive feedback as they correct invalid fields. */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentFramework(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f8f9fc', minHeight: '100vh', padding: '30px' }}>
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h2 mb-1 fw-bold" style={{ color: '#2c3e50' }}>
            <i className="fas fa-shield-alt me-3" style={{ color: '#3498db' }}></i>
            Compliance Frameworks
          </h1>
          <p className="text-muted mb-0">Manage compliance framework options for UAR campaigns</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary shadow-sm"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: '600'
            }}
          >
            {refreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Refreshing...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Refresh
              </>
            )}
          </button>
          <button
            className="btn btn-primary shadow-sm"
            onClick={handleAdd}
            style={{
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: '600'
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Framework
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
        </div>
      )}

      {/* Frameworks Table */}
      <div className="card shadow-lg border-0 mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <div className="card-header py-4" style={{
          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h5 className="m-0 fw-bold" style={{ color: '#2d3748' }}>
            <i className="fas fa-list me-2"></i>
            All Compliance Frameworks
          </h5>
        </div>
        <div className="card-body" style={{ padding: '0' }}>
          {frameworks.length === 0 ? (
            <div className="alert alert-info text-center m-4" style={{ borderRadius: '10px' }}>
              <i className="fas fa-info-circle me-2"></i>
              No compliance frameworks found. Add your first framework to get started!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th className="border-0 py-3 px-4" style={{ fontWeight: '700', color: '#2c3e50' }}>Framework Name</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Description</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Status</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {frameworks.map((framework, index) => (
                    <tr key={framework.framework_id} style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fc',
                      transition: 'all 0.3s ease'
                    }}>
                      <td className="py-3 px-4">
                        <strong style={{ color: '#2c3e50', fontSize: '1.05rem' }}>{framework.framework_name}</strong>
                      </td>
                      <td className="py-3">
                        <span className="text-muted">{framework.framework_description || 'N/A'}</span>
                      </td>
                      <td className="py-3">
                        {framework.is_active === true || framework.is_active === 'Y' ? (
                          <span className="badge" style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#fff',
                            background: '#48bb78'
                          }}>
                            <i className="fas fa-check-circle me-1"></i>Active
                          </span>
                        ) : (
                          <span className="badge" style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#fff',
                            background: '#718096'
                          }}>
                            <i className="fas fa-times-circle me-1"></i>Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(framework)}
                          style={{ borderRadius: '8px' }}
                        >
                          <i className="fas fa-edit me-1"></i>Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(framework.framework_name)}
                          style={{ borderRadius: '8px' }}
                        >
                          <i className="fas fa-trash me-1"></i>Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderTopLeftRadius: '15px',
                borderTopRightRadius: '15px'
              }}>
                <h5 className="modal-title fw-bold">
                  <i className={`fas ${isEdit ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                  {isEdit ? 'Edit Compliance Framework' : 'Add New Compliance Framework'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Framework Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.framework_name ? 'is-invalid' : ''}`}
                      name="framework_name"
                      value={currentFramework.framework_name}
                      onChange={handleInputChange}
                      disabled={isEdit}
                      placeholder="e.g., SOX, GDPR, HIPAA (no spaces)"
                      style={{ borderRadius: '10px' }}
                    />
                    {errors.framework_name && (
                      <div className="invalid-feedback">{errors.framework_name}</div>
                    )}
                    <div className="form-text">
                      <i className="fas fa-info-circle me-1"></i>
                      Framework Name is used as a unique identifier and cannot contain spaces. Cannot be changed after creation.
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <textarea
                      className="form-control"
                      name="framework_description"
                      value={currentFramework.framework_description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter framework description"
                      style={{ borderRadius: '10px' }}
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Status</label>
                    <select
                      className="form-select"
                      name="is_active"
                      value={currentFramework.is_active}
                      onChange={handleInputChange}
                      style={{ borderRadius: '10px' }}
                    >
                      <option value="Y">Active</option>
                      <option value="N">Inactive</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-save me-2"></i>
                  {isEdit ? 'Update Framework' : 'Create Framework'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1050,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '100%', margin: '50px auto', borderRadius: '15px' }}>
              <div className="modal-header bg-danger text-white" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Delete Compliance Framework
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-danger mb-4">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone. The compliance framework will be permanently removed from the system.
                </div>

                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <strong>Compliance Framework to Delete</strong>
                  </div>
                  <div className="card-body">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: '150px' }}>Framework Name:</td>
                          <td><span className="badge bg-secondary fs-6">{deleteTarget}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="alert alert-warning mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Note:</strong> Compliance frameworks that are currently in use by applications cannot be deleted. You must remove the compliance flags from all associated applications first.
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  disabled={deleting}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={deleting}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>Delete Framework
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                color: 'white',
                borderTopLeftRadius: '15px',
                borderTopRightRadius: '15px'
              }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Cannot Delete
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowErrorModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="text-center">
                  <i className="fas fa-ban fa-3x text-warning mb-3"></i>
                  <p className="mb-0">{errorModalMessage}</p>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowErrorModal(false)}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-check me-2"></i>OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceFrameworks;
